/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-03T22:30:00+05:00
Intent: Read a campaign's receipt submissions (image, what the reader found, rule results, review state) from the gateway into one shape for the Receipt submissions card, and build the approve / reject request.
*/

/**
 * The gateway contract this card is built against (the backend side is not live yet):
 *
 *   CONTEST_RECEIPT_SUBMISSIONS  { contest_id, page, per_page, status?, level_id? }
 *     -> { items: ReceiptSubmission[], total, counts: { review, approved, rejected } }
 *   CONTEST_RECEIPT_REVIEW       { contest_id, submission_id, decision: 'approve' | 'reject', reason? }
 *     -> the submission after the decision
 *
 * The server answers only for the signed-in business's own campaigns. Field names below accept
 * the obvious alternatives, so a small naming difference on the server does not blank the card.
 */

export type ReceiptReviewState = 'review' | 'processing' | 'approved' | 'rejected';
export type ReceiptStatusFilter = '' | 'review' | 'approved' | 'rejected';

export interface ReceiptLineItem {
  name: string;
  quantity: number | null;
  price: number | null;
  /** The campaign product this line counted as, when it matched one. */
  matched_product: string | null;
}

export interface ReceiptRuleResult {
  /** retailer, products, min_quantity, min_spend, currency, purchase_window, duplicate, confidence, attempts… */
  rule: string;
  passed: boolean | null;
  /** What the rule asked for and what the receipt had, as the server words it. */
  expected: string | null;
  actual: string | null;
  detail: string | null;
}

export interface ReceiptSubmission {
  id: string;
  level_id: string | null;
  level_name: string;
  level_name_ar: string;
  participant_name: string;
  phone_number: string;
  submitted_at: string | number | null;
  source: string | null;
  image_url: string | null;
  state: ReceiptReviewState;
  /** The raw status, for anything the four states above do not cover. */
  status: string;
  shop: string | null;
  receipt_date: string | null;
  total: number | null;
  currency: string | null;
  items: ReceiptLineItem[];
  rules: ReceiptRuleResult[];
  confidence: number | null;
  reason: string | null;
  reviewed_by: string | null;
  reviewed_at: string | number | null;
}

export interface ReceiptSubmissionPage {
  items: ReceiptSubmission[];
  total: number;
  counts: Record<Exclude<ReceiptStatusFilter, ''>, number> | null;
  /** false while the gateway has no receipt review action (the answer says available: false). */
  available: boolean;
}

const text = (...values: unknown[]): string | null => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const s = String(value).trim();
    if (s) return s;
  }
  return null;
};

const num = (...values: unknown[]): number | null => {
  for (const value of values) {
    if (value === null || value === undefined || String(value).trim() === '') continue;
    const n = Number(String(value).replace(/,/g, ''));
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const asObject = (value: unknown): Record<string, any> => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, any>;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const asList = (value: unknown): any[] => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

/** The four states the card shows. Anything still waiting for a person is "review". */
export function receiptState(status: unknown): ReceiptReviewState {
  const s = String(status ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (['approved', 'accepted', 'completed', 'passed', 'verified', 'auto_approved'].includes(s)) return 'approved';
  if (['rejected', 'declined', 'failed', 'auto_rejected', 'refused'].includes(s)) return 'rejected';
  if (['processing', 'queued', 'reading', 'uploaded', 'ocr_pending'].includes(s)) return 'processing';
  return 'review';
}

function readImage(row: Record<string, any>): string | null {
  const image = row['image'] ?? row['receipt_image'] ?? row['file'];
  const url = text(
    row['image_url'], row['receipt_url'], row['receipt_image_url'], row['file_url'],
    typeof image === 'string' ? image : image?.url ?? image?.path,
  );
  // Only web addresses and inline images reach the <img>; anything else is not shown.
  return url && /^(https?:\/\/|data:image\/)/i.test(url) ? url : null;
}

function readItems(read: Record<string, any>, row: Record<string, any>): ReceiptLineItem[] {
  return asList(read['items'] ?? read['line_items'] ?? row['items'] ?? row['line_items'])
    .map((item: any) => {
      const o = typeof item === 'object' && item ? item : { name: item };
      const matched = o.matched_product ?? o.product ?? o.matched;
      return {
        name: text(o.name, o.description, o.text) ?? '',
        quantity: num(o.quantity, o.qty),
        price: num(o.total, o.price, o.amount),
        matched_product: typeof matched === 'object' && matched
          ? text(matched.name)
          : matched === true ? text(o.name) : text(matched === false ? null : matched),
      };
    })
    .filter((item) => item.name !== '');
}

function readRules(row: Record<string, any>): ReceiptRuleResult[] {
  const raw = row['rule_results'] ?? row['rules'] ?? row['checks'];
  // Either a list of { rule, passed } or a map { rule: { passed } } / { rule: boolean }.
  const list = Array.isArray(raw) || typeof raw === 'string'
    ? asList(raw)
    : Object.entries(asObject(raw)).map(([rule, value]) =>
      typeof value === 'object' && value ? { rule, ...value } : { rule, passed: value });
  return list
    .map((r: any) => {
      const passed = r?.passed ?? r?.pass ?? r?.ok ?? r?.result;
      return {
        rule: String(r?.rule ?? r?.key ?? r?.name ?? '').trim(),
        passed: typeof passed === 'boolean' ? passed
          : passed === 'pass' || passed === 'passed' ? true
            : passed === 'fail' || passed === 'failed' ? false : null,
        expected: text(r?.expected, r?.required),
        actual: text(r?.actual, r?.found, r?.value),
        detail: text(r?.detail, r?.message, r?.reason),
      };
    })
    .filter((r) => r.rule !== '');
}

export function readReceiptSubmission(raw: any): ReceiptSubmission {
  const row = asObject(raw);
  const read = asObject(row['read'] ?? row['extracted'] ?? row['ocr'] ?? row['receipt_data']);
  const participant = asObject(row['participant'] ?? row['user']);
  const level = asObject(row['level']);
  const status = text(row['review_status'], row['status'], row['outcome']) ?? '';
  const confidence = num(row['confidence'], read['confidence']);
  return {
    id: String(row['id'] ?? row['submission_id'] ?? row['receipt_submission_id'] ?? ''),
    level_id: text(row['level_id'], level['id']),
    level_name: text(row['level_name'], level['name']) ?? '',
    level_name_ar: text(row['level_name_ar'], row['arabic_level_name'], level['arabic_name']) ?? '',
    participant_name: text(row['participant_name'], participant['name'], participant['full_name']) ?? '',
    phone_number: text(row['phone_number'], participant['phone_number'], participant['phone']) ?? '',
    submitted_at: row['submitted_at'] ?? row['created_at'] ?? null,
    source: text(row['source']),
    image_url: readImage(row),
    state: receiptState(status),
    status,
    shop: text(read['shop'], read['shop_name'], read['retailer'], read['merchant'], read['store'], row['shop_name'], row['retailer']),
    receipt_date: text(read['receipt_date'], read['purchase_date'], read['date'], row['receipt_date'], row['purchase_date']),
    total: num(read['total'], read['amount'], read['receipt_total'], row['total'], row['receipt_total']),
    currency: text(read['currency'], row['currency'])?.toUpperCase() ?? null,
    items: readItems(read, row),
    rules: readRules(row),
    // Sent as 0-1 or as a percent.
    confidence: confidence === null ? null : confidence > 1 ? confidence / 100 : confidence,
    reason: text(row['reason'], row['rejection_reason'], row['review_reason'], row['reason_code']),
    reviewed_by: text(row['reviewed_by_name'], row['reviewed_by']),
    reviewed_at: row['reviewed_at'] ?? null,
  };
}

export function readReceiptSubmissionPage(response: any): ReceiptSubmissionPage {
  const body = asObject(response?.items ? response : response?.data?.items ? response.data : response);
  const items = asList(body['items']).map(readReceiptSubmission).filter((s) => s.id !== '');
  const rawCounts = asObject(body['counts']);
  const counts = Object.keys(rawCounts).length
    ? {
      review: num(rawCounts['review'], rawCounts['needs_review'], rawCounts['pending']) ?? 0,
      approved: num(rawCounts['approved']) ?? 0,
      rejected: num(rawCounts['rejected']) ?? 0,
    }
    : null;
  return {
    items,
    total: num(body['total'], body['itemsTotal'], body['items_total']) ?? items.length,
    counts,
    available: body['available'] !== false,
  };
}

/** The status filter as the server reads it. */
export function receiptStatusParam(filter: ReceiptStatusFilter): string | undefined {
  return filter === 'review' ? 'needs_review' : filter || undefined;
}

export interface ReceiptReviewRequest {
  contest_id: string;
  submission_id: string;
  decision: 'approve' | 'reject';
  reason?: string;
}

/** The approve / reject body. A rejection needs a reason the participant can act on. */
export function buildReceiptReview(
  contestId: string, submissionId: string, decision: 'approve' | 'reject', reason?: string | null,
): ReceiptReviewRequest | null {
  const why = String(reason ?? '').trim();
  if (!contestId || !submissionId) return null;
  if (decision === 'reject' && !why) return null;
  return { contest_id: contestId, submission_id: submissionId, decision, ...(why ? { reason: why } : {}) };
}

/** Passed / total of the rules that were checked, e.g. { passed: 4, total: 5 }. */
export function ruleTally(submission: ReceiptSubmission): { passed: number; total: number } {
  const checked = submission.rules.filter((r) => r.passed !== null);
  return { passed: checked.filter((r) => r.passed).length, total: checked.length };
}
